import { Direction } from '@angular/cdk/bidi';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { translate } from '@ngneat/transloco';
import { filter, Subscription } from 'rxjs';

import { UtilsService } from '../../services/utils/utils.service';
import { CommonUtilsService } from '../../services/commonutils/common-utils.service';

import type { AngularEditorConfig } from 'angular-editor';
@Component({
    selector: 'app-rich-text-editor',
    templateUrl: './rich-text-editor.component.html',
    styleUrls: ['./rich-text-editor.component.scss'],
    standalone: false,
})
export class RichTextEditorComponent implements OnInit, OnDestroy {
    @Input() enableEncryptDecrypt: boolean;
    @Input() placeholder: string;
    @Input() title: string;
    @Input() model: any;
    @Input() id: string;
    @Input() name: string;
    @Input() required: boolean;
    @Input()
    editable: boolean;
    @Input() errors: any[];
    @Input() maxLength: number;
    @Input() hint: string;
    @Input() textDirection?: Direction;

    @Output() modelChange = new EventEmitter();
    control = new UntypedFormControl('');
    valueSubscription: Subscription;
    showToolbar = false;
    hideAllActions: any = [
        [
            'undo',
            'redo',
            'bold',
            'italic',
            'underline',
            'strikeThrough',
            'subscript',
            'superscript',
            'justifyLeft',
            'justifyCenter',
            'justifyRight',
            'justifyFull',
            'indent',
            'outdent',
            'insertUnorderedList',
            'insertOrderedList',
            'heading',
            'fontName',
        ],
        [
            'fontSize',
            'textColor',
            'backgroundColor',
            'customClasses',
            'link',
            'unlink',
            'insertImage',
            'insertVideo',
            'insertHorizontalRule',
            'removeFormat',
            'toggleEditorMode',
        ],
    ];
    hideMinimumActions: any = [['fontName'], ['insertImage', 'insertVideo']];
    editorConfig: AngularEditorConfig = {
        editable: false,
        sanitize: false,
        toolbarHiddenButtons: [['fontName'], ['insertImage', 'insertVideo']],
    };
    dir: Direction = 'ltr';

    constructor(private _utils: UtilsService) {}

    modelChanged = (content: any) => {
        const processedContent = CommonUtilsService.applyScopedStyles(content);
        this.checkArabic(processedContent);
        if (this.validateContent(processedContent)) {
            this._utils.alertError('The content is malformed. <script> or <py-script> tags are not allowed.');
            return;
        }
        if (this.enableEncryptDecrypt) {
            this.modelChange.emit(this.enableEncryptDecrypt ? encodeURIComponent(processedContent) : processedContent);
        } else {
            this.modelChange.emit(processedContent);
        }
    };

    validateContent = (content: any) => {
        return (
            content.includes('<script') ||
            content.includes('<py-script') ||
            content.includes('&lt;script') ||
            content.includes('&lt;py-script')
        );
    };

    showFormatToolbar = () => {
        this.showToolbar = !this.showToolbar;
        const hiddenButtons = this.showToolbar ? this.hideMinimumActions : this.hideAllActions;
        // Spread a new object so Angular's change detection picks up the updated toolbarHiddenButtons reference
        this.editorConfig = { ...this.editorConfig, toolbarHiddenButtons: hiddenButtons };
    };

    checkArabic = (content?: string) =>
        (this.dir = this.textDirection || (/[\u0600-\u06FF]/.test(content !== undefined ? content : this.model) ? 'rtl' : 'ltr'));

    private safeDecodeURIComponent(value: string): string {
        if (!value) return '';
        try {
            const safeValue = value.replace(/%(?![0-9A-Fa-f]{2})/g, '%25');
            return decodeURIComponent(safeValue);
        } catch {
            return value;
        }
    }

    ngOnInit() {
        // Build the initial config with a new object reference so Angular's change detection works correctly
        this.editorConfig = {
            ...this.editorConfig,
            editable: this.editable,
            showToolbar: this.editable,
            toolbarHiddenButtons: this.enableEncryptDecrypt ? this.hideAllActions : this.editorConfig.toolbarHiddenButtons,
        };
        // Decode it first so the editor always works with plain HTML internally.
        if (this.enableEncryptDecrypt && this.model) {
            this.model = this.safeDecodeURIComponent(this.model as string);
        }
        this.model = CommonUtilsService.applyScopedStyles(this.model as string);
        this.model?.length > 0 && this.control.setValue(this.model);
        this.valueSubscription = this.control.valueChanges
            .pipe(filter(data => data !== undefined))
            .subscribe(_event => this.modelChanged(_event));
    }

    ngAfterViewInit(): void {
        const buttons: any = document.getElementsByClassName('angular-editor-button');
        for (const button of buttons) {
            button?.title?.length > 0 && (button.title = translate(button.title));
        }
        this.checkArabic();
    }

    ngOnDestroy(): void {
        // Called once, before the instance is destroyed.
        this.valueSubscription && this.valueSubscription.unsubscribe();
    }
}
